import dev_config from './stage-config/dev_config';
import beta_config from './stage-config/beta_config';
import gamma_config from './stage-config/gamma_config';
import prod_config from './stage-config/prod_config';
import { getCurrentStage, PORTAL_ROOT, LEGACY_ROOT, isLocalHost } from './domain';

// Export configuration (will be set at runtime)
export let config = beta_config; // Default

/**
 * Wraps a promise with a timeout
 */
function timeout(ms: number, promise: Promise<any>): Promise<any> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error('timeout'));
    }, ms);

    promise.then(
      (value) => {
        clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        clearTimeout(timeoutId);
        reject(error);
      }
    );
  });
}

/**
 * Detect stage from hostname.
 *
 * Delegates to resolveDomain() in domain.ts, which understands both the new
 * portal structure (<system>.[stage.]goh.lab126.amazon.dev) and the legacy
 * [stage.]msa.lab126.amazon.dev hosts, plus localhost (defaults to beta).
 */
function detectStageFromHostname(): string {
  return getCurrentStage('beta');
}

/**
 * Force the OAuth redirect URLs to the CURRENT origin.
 *
 * A single S3 config (msa_config.json) is served to every domain that shares a
 * bucket — for beta that is the portal (beta.goh...), the per-system hosts
 * (msa.beta.goh..., atlas.beta.goh...) AND the legacy beta.msa.lab126 host. A
 * hardcoded `redirectSignIn` can only ever name one of them, so Cognito would
 * bounce every other domain back to that one host after login. Deriving the
 * redirect from `window.location.origin` at runtime makes login return to
 * whichever domain the user actually came from — correct for all of them, with
 * no per-domain config. The origin must also be registered in the Cognito app
 * client's callback/logout allowlist.
 */
function normalizeOAuthRedirects(cfg: any): any {
  if (typeof window === 'undefined') return cfg;
  const oauth = cfg?.AMPLIFY_CONFIG?.oauth;
  if (oauth) {
    oauth.redirectSignIn = window.location.origin;
    oauth.redirectSignOut = window.location.origin;
  }
  return cfg;
}

function normalizeCookieStorage(cfg: any): any {
  if (typeof window === 'undefined') return cfg;
  const amplify = cfg?.AMPLIFY_CONFIG;
  if (!amplify) return cfg;

  // Snapshot AMPLIFY_CONFIG to avoid getter re-evaluation losing mutations
  cfg = { ...cfg, AMPLIFY_CONFIG: { ...amplify } };

  const hostname = window.location.hostname;
  const isLocal = isLocalHost(hostname);
  const isNewDomain = hostname.endsWith(PORTAL_ROOT);

  if (isLocal) {
    cfg.AMPLIFY_CONFIG.cookieStorage = {
      domain: hostname,
      secure: false,
      path: '/',
      sameSite: 'lax',
    };
  } else if (isNewDomain) {
    const stage = getCurrentStage();
    cfg.AMPLIFY_CONFIG.cookieStorage = {
      domain: stage === 'prod' ? `.${PORTAL_ROOT}` : `.${stage}.${PORTAL_ROOT}`,
      secure: true,
      path: '/',
      sameSite: 'lax',
    };
  } else if (hostname.endsWith(LEGACY_ROOT)) {
    const stage = getCurrentStage();
    cfg.AMPLIFY_CONFIG.cookieStorage = {
      domain: stage === 'prod' ? `.${LEGACY_ROOT}` : `.${stage}.${LEGACY_ROOT}`,
      secure: true,
      path: '/',
      sameSite: 'lax',
    };
  } else {
    delete cfg.AMPLIFY_CONFIG.cookieStorage;
  }
  return cfg;
}

/**
 * Select configuration based on stage
 */
function getConfigForStage(stage: string) {
  console.log('[Config] Loading configuration for stage:', stage);

  if (typeof window !== 'undefined' && isLocalHost(window.location.hostname)) {
    console.log('[Config] Using dev configuration (localhost)');
    return dev_config;
  }

  switch (stage) {
    case 'gamma':
      console.log('[Config] Using gamma configuration');
      return gamma_config;
    case 'prod':
      console.log('[Config] Using prod configuration');
      return prod_config;
    case 'beta':
    default:
      console.log('[Config] Using beta configuration (default)');
      return beta_config;
  }
}

/**
 * Load configuration at runtime from S3 (via CloudFront)
 * Falls back to hostname-based detection and hardcoded configs if fetch fails
 */
export async function loadAppConfig() {
  console.log('[Config] Loading runtime configuration...');
  console.log('[Config] Current hostname:', window.location.hostname);
  
  try {
    const timestamp = new Date().getTime();
    const configPath = `/config/msa_config.json?timestamp=${timestamp}`;
    console.log('[Config] Fetching from:', configPath);
    
    const fetchedConfig = await timeout(
      1000,
      fetch(configPath, { cache: 'no-store' }).then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
    );
    
    config = normalizeCookieStorage(normalizeOAuthRedirects(fetchedConfig));
    console.log('[Config] ✅ Loaded runtime config from S3');
    console.log('[Config] Stage:', config.STAGE || 'not specified');
    console.log('[Config] OAuth redirect origin:', config?.AMPLIFY_CONFIG?.oauth?.redirectSignIn);
    return { config };
    
  } catch (error) {
    console.warn('[Config] ⚠️ Failed to load runtime config from S3');
    console.warn('[Config] Error:', error);
    
    // Fallback: Detect stage from hostname
    const detectedStage = detectStageFromHostname();
    console.log('[Config] Detected stage from hostname:', detectedStage);
    
    config = normalizeCookieStorage(normalizeOAuthRedirects(getConfigForStage(detectedStage)));

    console.log('[Config] Using fallback config for stage:', detectedStage);
    return { config };
  }
}
